import { ofType } from 'redux-observable';
import { concat, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { DEVICE_DESKTOP, DEVICE_MOBILE } from '../../../common/constants/device';
import {
  FILTER_CONFIG_DURATION,
  MINIMUM_LANGUAGE_COUNT,
  QUERY_DURATION_KEY,
  QUERY_LANGUAGE_KEY,
  QUERY_SEARCH_KEY,
  QUERY_TIME_KEY,
} from '../../../common/constants/queryParams';
import { SEARCH_AI_ENDPOINT_URL, SEARCH_GEN_AI_REQUEST_TIMEOUT } from '../../../common/constants/request';

import { getMainPlayerWidget } from '../../../common/components/Player/helpers';
import { RootContainers } from '../../../common/helpers';
import { dateSubtract } from '../../../common/helpers/dateTime';
import { offset, scrollYByElement } from '../../../common/helpers/dom';
import logger from '../../../common/helpers/logger';
import { tmPostRequest } from '../../../common/helpers/request';
import { getUniqueAppId } from '../../../common/helpers/string';
import { getFormattedVideo } from '../../../common/helpers/video';
import * as commonSelectors from '../../../common/redux/selectors/common';
import {
  clearSearchAiResultAction,
  errorSearchAiRequestAction,
  searchAiRequestAction,
  searchAiRequestEventAction,
  setSearchAiQueryAction,
  setSearchAiRequestAction,
} from '../slices/searchAi';
import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';

export default (action$, state$) =>
  action$.pipe(
    ofType(searchAiRequestAction.type),
    filter(({ payload: { query } }) => !!query[QUERY_SEARCH_KEY] && commonSelectors.genAISelector(state$.value)),
    switchMap(({ payload }) => {
      const querySearch = payload.query[QUERY_SEARCH_KEY];
      const queryLanguage = payload.query[QUERY_LANGUAGE_KEY];
      const time = payload.query[QUERY_TIME_KEY];
      const duration = payload.query[QUERY_DURATION_KEY];

      const id = commonSelectors.idSelector(state$.value);
      const player = commonSelectors.playerSelector(state$.value);
      const publisher = commonSelectors.publisherSelector(state$.value);
      const languages = commonSelectors.languagesSelector(state$.value);
      const genAICount = commonSelectors.genAICountSelector(state$.value);

      let languageFilters;
      if (languages.length && languages.length < MINIMUM_LANGUAGE_COUNT) {
        languageFilters = languages[0]?.value && [languages[0].value];
      } else if (languages.length >= MINIMUM_LANGUAGE_COUNT && !queryLanguage) {
        languageFilters = languages.filter((language) => !!language.value).map((language) => language.value);
      } else if (languages.length >= MINIMUM_LANGUAGE_COUNT && queryLanguage) {
        const isQueryLanguageExist = languages.find((language$) => language$.value === queryLanguage);
        languageFilters = isQueryLanguageExist && [queryLanguage];
      }

      const ranges = [];
      if (time) {
        const dateNow = new Date();

        ranges.push({
          from: dateSubtract(dateNow, -1, time).getTime(),
          to: dateNow.getTime(),
          of: 'videoCreationDate',
        });
      }

      if (duration) {
        const { from, to } = FILTER_CONFIG_DURATION.find((item) => item.value === duration);

        ranges.push({
          from,
          to,
          of: 'videoLength',
        });
      }

      const startTime = Date.now();

      const stream$ = tmPostRequest({
        timeout: SEARCH_GEN_AI_REQUEST_TIMEOUT,
        url: SEARCH_AI_ENDPOINT_URL,
        body: {
          publisherId: publisher.slug,
          widgetId: player.name,
          query: querySearch,
          size: genAICount,
          ranges: ranges.length ? ranges : undefined,
          dev: isMobileUserAgent ? DEVICE_MOBILE : DEVICE_DESKTOP,
          languageFilters,
        },
      }).pipe(
        map(({ status, response }) => {
          const payloadRes = {
            id: getUniqueAppId(),
            answer: '',
            playlist: [],
            playerPropsPassThrow: {},
            totalCount: 0,
            ids: [],
          };

          if (status === 200 && response.totalCount) {
            const { playlist, ...playerPropsPassThrow } = response.data;

            payloadRes.answer = response.answer;
            payloadRes.totalCount = response.totalCount;
            payloadRes.ids = response.ids;
            payloadRes.playlist = playlist.map((video) =>
              getFormattedVideo(video, {
                signedUrlExp: playerPropsPassThrow.signedUrlExp,
              }),
            );
            payloadRes.playerPropsPassThrow = playerPropsPassThrow;

            scrollYByElement(window, offset(RootContainers.getRootContainer()).top);
          }

          const LWP = getMainPlayerWidget();
          LWP.sendStatsGenAiSearch(
            payloadRes.id,
            querySearch,
            Date.now() - startTime,
            payloadRes.ids,
            payloadRes.totalCount,
            id,
          );

          return setSearchAiRequestAction(payloadRes);
        }),
        catchError((error) => {
          logger.error(
            'Error of search ai request',
            'src/modules/Search/redux/epics/searchAi.js',
            SEARCH_AI_ENDPOINT_URL,
            error,
          );

          return of(errorSearchAiRequestAction());
        }),
      );

      const actions = [
        of(setSearchAiQueryAction(payload.query)),
        of(searchAiRequestEventAction()),
        of(clearSearchAiResultAction()),
      ];

      actions.push(stream$);

      return concat(...actions);
    }),
  );
